import { Progress } from "@/common/components/ui/progress";
import { Badge } from "@/common/components/ui/badge";
import { CalendarDays, Clock } from "lucide-react";
import { SectionTitle } from "../utils/helpers.jsx";

export default function LiveSessionAttendance({
  attendance,
  isTableView = false,
}) {
  if (!attendance) return null;

  // Table view format
  if (isTableView) {
    return (
      <div className="mb-6 md:mb-8">
        <SectionTitle title="📋 Student Attendance Report" />
        <div className="overflow-x-auto print:overflow-visible">
          <table className="w-full border-collapse border border-gray-200 print:border-collapse">
            <thead>
              <tr className="bg-gray-50 print:bg-gray-100">
                <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                  Session Name
                </th>
                <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                  Total Training Days
                </th>
                <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                  Total Days Attended
                </th>
                <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                  Percentage Attendance
                </th>
                <th className="px-4 py-2 font-medium text-left text-slate-600 border print:font-bold print:text-black">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {attendance.sessions.map((session) => (
                <tr
                  key={session.sessionId}
                  className="print:break-inside-avoid"
                >
                  <td className="px-4 py-3 border font-medium print:py-2">
                    {session.title}
                  </td>
                  <td className="px-4 py-3 border text-center print:py-2">
                    {session.totalDays}
                  </td>
                  <td className="px-4 py-3 border text-center print:py-2">
                    {session.attendedDays}
                  </td>
                  <td className="px-4 py-3 border text-center print:py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-3 bg-gray-200 rounded-full hidden md:block print:block print:h-2">
                        <div
                          className={`h-3 rounded-full print:h-2 ${
                            session.percentage >= 75
                              ? "bg-green-500"
                              : session.percentage >= 50
                              ? "bg-yellow-500"
                              : "bg-red-500"
                          }`}
                          style={{
                            width: `${Math.round(session.percentage)}%`,
                          }}
                        ></div>
                      </div>
                      <span className="font-medium print:font-bold">
                        {Math.round(session.percentage)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 border print:py-2">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        session.percentage >= 75
                          ? "bg-green-100 text-green-800"
                          : session.percentage >= 50
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      } print:font-bold print:py-0`}
                    >
                      {session.percentage >= 75
                        ? "Good"
                        : session.percentage >= 50
                        ? "Average"
                        : "Poor"}
                    </span>
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-medium print:bg-gray-100 print:font-bold">
                <td className="px-4 py-3 border print:py-2">Overall</td>
                <td className="px-4 py-3 border text-center print:py-2">-</td>
                <td className="px-4 py-3 border text-center print:py-2">-</td>
                <td className="px-4 py-3 border text-center print:py-2">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-16 h-3 bg-gray-200 rounded-full hidden md:block print:block print:h-2">
                      <div
                        className={`h-3 rounded-full print:h-2 ${
                          attendance.averagePercentage >= 75
                            ? "bg-green-500"
                            : attendance.averagePercentage >= 50
                            ? "bg-yellow-500"
                            : "bg-red-500"
                        }`}
                        style={{
                          width: `${Math.round(attendance.averagePercentage)}%`,
                        }}
                      ></div>
                    </div>
                    <span className="font-medium print:font-bold">
                      {Math.round(attendance.averagePercentage)}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 border print:py-2">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      attendance.averagePercentage >= 75
                        ? "bg-green-100 text-green-800"
                        : attendance.averagePercentage >= 50
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    } print:font-bold print:py-0`}
                  >
                    {attendance.averagePercentage >= 75
                      ? "Good"
                      : attendance.averagePercentage >= 50
                      ? "Average"
                      : "Poor"}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-gray-500 print:text-xs print:mt-2">
          Regular attendance is critical for academic success. Attendance below
          75% may affect overall performance.
        </p>
      </div>
    );
  }

  // Original card view
  return (
    <div className="mb-6 md:mb-8">
      <SectionTitle title="Live Session Attendance" />

      {/* Overall Attendance Card */}
      <div className="p-4 mb-4 border rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <CalendarDays className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Average Attendance</p>
              <p className="text-2xl font-bold text-slate-900">
                {Math.round(attendance.averagePercentage)}%
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 px-3 py-1 text-sm text-blue-700 bg-blue-100 rounded-full">
              <Clock className="w-4 h-4" />
              <span>Overall</span>
            </div>
          </div>
        </div>
      </div>

      {/* Individual Sessions */}
      <div className="grid gap-4 md:grid-cols-2">
        {attendance.sessions.map((session) => (
          <div
            key={session.sessionId}
            className="p-4 border rounded-lg shadow-sm bg-white hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-slate-900 line-clamp-1">
                    {session.title}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {session.attendedDays} of {session.totalDays} days
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className={`${
                    session.percentage >= 75
                      ? "bg-green-50 text-green-700 border-green-200"
                      : session.percentage >= 50
                      ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                      : "bg-red-50 text-red-700 border-red-200"
                  }`}
                >
                  {Math.round(session.percentage)}%
                </Badge>
              </div>

              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Attendance Progress</span>
                  <span>{Math.round(session.percentage)}%</span>
                </div>
                <Progress
                  value={session.percentage}
                  className="h-2"
                  style={{
                    backgroundColor: "#e2e8f0",
                    "--progress-color":
                      session.percentage >= 75
                        ? "#22c55e"
                        : session.percentage >= 50
                        ? "#f59e0b"
                        : "#ef4444",
                    "--progress-value": `${session.percentage}%`,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
